import React from "react";
import { Box, Card, Chip } from "@mui/joy";
import DarkTypography from "../../common/DarkTypography";
import MotifCell from "./MotifCell";
import LeaveValidationRequestActions from "./LeaveValidationRequestActions";
import type { LeaveRequest } from "../../../api/leave/Leave.typed";
import {
  formatLeaveDurationLabel,
  formatLeavePeriod,
  getStatusChip,
} from "../../../utils/leaveHelpers";
import { shouldDisplayLeaveMotifForUi } from "../../../utils/planningLeaveHelpers";
import { hrMobileRequestCardSx, textTokens } from "../../../styles/globalStyles";
import useAppSettings from "../../../contexts/app/appSettings";
import type { LeaveValidationRequestsTableProps } from "./LeaveValidationRequestsTable";

type MobileCardProps = Pick<
  LeaveValidationRequestsTableProps,
  | "leaveTypes"
  | "getUserName"
  | "formatDate"
  | "onApprove"
  | "onReject"
  | "onCancel"
  | "onApproveCancellation"
  | "onRejectCancellation"
  | "onOpenReason"
  | "canValidate"
> & {
  request: LeaveRequest;
  companyTimeZone?: string;
};

function LeaveValidationMobileCard({
  request,
  leaveTypes,
  getUserName,
  formatDate,
  onApprove,
  onReject,
  onCancel,
  onApproveCancellation,
  onRejectCancellation,
  onOpenReason,
  canValidate = true,
  companyTimeZone,
}: MobileCardProps): React.ReactElement {
  const statusInfo = getStatusChip(request.status);
  const showMotif = shouldDisplayLeaveMotifForUi(request, leaveTypes);
  const typeName = leaveTypes.find((t) => t.ID === request.type_id)?.Name || "—";

  return (
    <Card
      sx={{
        ...hrMobileRequestCardSx,
        mb: 0,
        minWidth: 0,
        maxWidth: "100%",
        overflow: "visible",
        touchAction: "pan-y",
        p: { xs: 0.85, sm: 1.5 },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: { xs: 0.65, sm: 1 },
          mb: { xs: 0.65, sm: 1 },
          flexWrap: "nowrap",
        }}
      >
        <Box sx={{ flex: "1 1 auto", minWidth: 0 }}>
          <DarkTypography
            level="body-sm"
            sx={{ fontWeight: "bold", fontSize: { xs: "0.8rem", sm: undefined } }}
          >
            {getUserName(request.user_id) || `Utilisateur #${request.user_id}`}
          </DarkTypography>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            flexWrap: "nowrap",
            flexShrink: 0,
            justifyContent: "flex-end",
            minWidth: 0,
          }}
        >
          {statusInfo ? (
            <Chip
              variant="soft"
              color={statusInfo.color}
              size="sm"
              sx={{ flexShrink: 1, minWidth: 0, maxWidth: "100%", px: 0.5 }}
              title={statusInfo.name}
            >
              <Box
                component="span"
                sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
              >
                {statusInfo.name}
              </Box>
            </Chip>
          ) : null}
          <LeaveValidationRequestActions
            request={request}
            canValidate={canValidate}
            onApprove={onApprove}
            onReject={onReject}
            onCancel={onCancel}
            onApproveCancellation={onApproveCancellation}
            onRejectCancellation={onRejectCancellation}
            compact
          />
        </Box>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: { xs: 0.5, sm: 0.85 },
          columnGap: { xs: 0.85, sm: 1.15 },
          mb: showMotif && request.reason ? { xs: 0.5, sm: 0.85 } : 0,
          "& > *": { minWidth: 0 },
        }}
      >
        <Box>
          <DarkTypography
            level="body-xs"
            sx={{ color: textTokens.color.faint, lineHeight: 1.15, mb: 0.2 }}
          >
            Type
          </DarkTypography>
          <DarkTypography level="body-xs" sx={{ wordBreak: "break-word", lineHeight: 1.25 }}>
            {typeName}
          </DarkTypography>
        </Box>
        <Box>
          <DarkTypography
            level="body-xs"
            sx={{ color: textTokens.color.faint, lineHeight: 1.15, mb: 0.2 }}
          >
            Durée
          </DarkTypography>
          <DarkTypography
            level="body-xs"
            sx={{ fontWeight: "bold", wordBreak: "break-word", lineHeight: 1.25 }}
          >
            {formatLeaveDurationLabel(request, companyTimeZone)}
          </DarkTypography>
        </Box>
        <Box sx={{ gridColumn: "1 / -1" }}>
          <DarkTypography
            level="body-xs"
            sx={{ color: textTokens.color.faint, lineHeight: 1.15, mb: 0.2 }}
          >
            Période
          </DarkTypography>
          <DarkTypography level="body-xs" sx={{ wordBreak: "break-word", lineHeight: 1.25 }}>
            {formatLeavePeriod(request, companyTimeZone)}
          </DarkTypography>
        </Box>
      </Box>

      {showMotif && (
        <Box sx={{ mb: { xs: 0.5, sm: 0.85 }, minWidth: 0 }}>
          <DarkTypography
            level="body-xs"
            sx={{ color: textTokens.color.faint, lineHeight: 1.15, mb: 0.2 }}
          >
            Motif
          </DarkTypography>
          <MotifCell
            text={request.reason || "—"}
            hasReason={!!request.reason}
            onOpen={() => onOpenReason(request.reason ?? "")}
            maxWidth="100%"
          />
        </Box>
      )}

      <DarkTypography level="body-xs" sx={{ color: textTokens.color.faint }}>
        Demandé {formatDate(request.created_at)}
      </DarkTypography>
    </Card>
  );
}

export default function LeaveValidationRequestsMobile(
  props: LeaveValidationRequestsTableProps,
): React.ReactElement {
  const { config } = useAppSettings();
  const companyTimeZone = config?.TimeZone;

  return (
    <Box
      sx={{
        display: { xs: "flex", md: "none" },
        flexDirection: "column",
        gap: { xs: 0.5, sm: 1 },
        minWidth: 0,
        maxWidth: "100%",
      }}
    >
      {props.requests.map((request) => (
        <LeaveValidationMobileCard
          key={request.id}
          request={request}
          companyTimeZone={companyTimeZone}
          leaveTypes={props.leaveTypes}
          getUserName={props.getUserName}
          formatDate={props.formatDate}
          onApprove={props.onApprove}
          onReject={props.onReject}
          onCancel={props.onCancel}
          onApproveCancellation={props.onApproveCancellation}
          onRejectCancellation={props.onRejectCancellation}
          onOpenReason={props.onOpenReason}
          canValidate={props.canValidate}
        />
      ))}
    </Box>
  );
}
